/**
 * Access control — the gates.
 *
 * Three ways to spend a capability, in ascending order of how much of the
 * product they hide:
 *
 *   `<Can>`        — a fragment of UI. Renders nothing when denied.
 *   `<RouteGuard>` — a whole route. Renders a 403 (or redirects) when denied.
 *   `useCan`       — everything else, in ordinary conditional logic.
 *
 * All three consult the same `POLICY`, so a nav item and the page it links to
 * cannot disagree about who is allowed in.
 */
import type { ReactNode } from 'react'
import { Navigate } from 'react-router'
import { Spinner } from 'tailwind-v4/components/spinner'
import { Button } from 'tailwind-v4/components/button'
import { useAccess } from './access.provider'
import type { Capability } from './access.types'

/**
 * Render `children` only when the user holds every listed capability.
 *
 * ```tsx
 * <Can capability="internal.engineering">
 *   <AskEngineeringButton />
 * </Can>
 *
 * <Can capability={['nav.marketing', 'plan.marketing']} fallback={<Upsell />}>
 *   <CampaignBuilder />
 * </Can>
 * ```
 *
 * An array requires ALL of them. For "any of", use `useAccess` and say so in
 * ordinary code rather than adding an operator prop nobody can read at a glance.
 */
export function Can({
  capability,
  children,
  fallback = null,
}: {
  capability: Capability | Capability[]
  children: ReactNode
  /** Shown when denied. Defaults to nothing at all. */
  fallback?: ReactNode
}) {
  const { can } = useAccess()
  const required = Array.isArray(capability) ? capability : [capability]
  const allowed = required.every((item) => can(item))
  return <>{allowed ? children : fallback}</>
}

/**
 * The route-level equivalent, wired in by `RoutesToItem` in `src/routes.tsx`
 * whenever a route declares a `capability`. Put a capability on a route and it
 * is enforced whether the user arrived by nav click, a bookmark, or a typed URL
 * — which is the whole point of doing this at the router rather than per page.
 *
 * While the subject is still resolving it shows a spinner rather than a 403:
 * a flash of "no access" on every cold load would be both wrong and alarming.
 */
export function RouteGuard({
  capability,
  children,
  redirectTo,
}: {
  capability: Capability
  children: ReactNode
  /** Send the user here instead of showing the 403. */
  redirectTo?: string
}) {
  const { can, subject } = useAccess()

  if (!subject.isResolved) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (can(capability)) return <>{children}</>
  if (redirectTo) return <Navigate to={redirectTo} replace />

  return <NoAccess />
}

/**
 * Shown when a route is denied. Deliberately says nothing about what was behind
 * it: naming the feature to someone who cannot reach it invites a support
 * ticket we would only answer with "your role does not include that".
 */
function NoAccess() {
  return (
    <div className="new-ui-theme flex h-full w-full items-center justify-center bg-background p-8 text-foreground">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-lg font-semibold">You don&apos;t have access</h1>
          <p className="text-sm text-muted-foreground">
            This page isn&apos;t available with your current permissions. Ask an
            administrator in your organisation if you think you should have
            access to it.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            window.history.back()
          }}
        >
          Go back
        </Button>
      </div>
    </div>
  )
}
